// Supported locales + detection. Kept free of React / store imports so the
// settings store can use it without an import cycle.

export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

/** Native names, shown in the language picker regardless of the active locale. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
};

/** Emoji flag per locale, shown next to the name in the language picker. */
export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Map a BCP 47 tag (e.g. `navigator.language`) onto a supported locale by
 * its primary subtag: `es-MX` → `es`. Anything unknown falls back to `en`.
 */
export function detectLocale(language: string | null | undefined): Locale {
  const primary = (language ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return isLocale(primary) ? primary : 'en';
}

/** The browser's preferred locale, or `en` outside a browser. */
export function browserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return detectLocale(navigator.language);
}
